import { Component, inject, signal, computed, OnInit, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

@Component({
	selector: 'app-modale-otp',
	standalone: true,
	imports: [CommonModule, FormsModule],
	templateUrl: './modale-otp.component.html'
})
export class ModaleOtpComponent implements OnInit, OnDestroy {
	activeModal = inject(NgbActiveModal);

	// Inputs
	@Input() mode: 'remote' | 'sms' = 'remote';
	@Input() phone: string = '';
	@Input() operationRef: string = '';
	
	@Output() onResend = new EventEmitter<void>();
	
	otpDigits = signal<string[]>(['', '', '', '', '', '']);
	loading = signal(false);
	error = signal(false);
    timerValue = signal(300); // 5 minutes in seconds
	private timerInterval: any;

    timerDisplay = computed(() => {
        const m = Math.floor(this.timerValue() / 60);
        const s = this.timerValue() % 60;
        return `${m}:${s < 10 ? '0' : ''}${s}`;
    });

	isOtpComplete = computed(() => {
		return this.otpDigits().every((d) => d.length === 1);
	});

	otpCode = computed(() => {
		return this.otpDigits().join('');
	});

	ngOnInit() {
        if (this.mode === 'sms') {
            this.startTimer();
        }
    }

	ngOnDestroy() {
		if (this.timerInterval) {
			clearInterval(this.timerInterval);
		}
	}

    startTimer() {
		if (this.timerInterval) clearInterval(this.timerInterval);
        this.timerInterval = setInterval(() => {
            this.timerValue.update(v => {
                if (v <= 0) {
                    clearInterval(this.timerInterval);
                    return 0;
                }
                return v - 1;
            });
        }, 1000);
    }

	resetTimer() {
		this.timerValue.set(300);
		this.startTimer();
		this.otpDigits.set(['', '', '', '', '', '']);
		this.error.set(false);
	}

	requestResend() {
		if (this.timerValue() === 0) {
			this.onResend.emit();
		}
	}

	onOtpInput(index: number, event: Event) {
		const input = event.target as HTMLInputElement;
		const rawVal = input.value.replace(/\D/g, '');

		// iOS autofill da SMS: inserisce tutti i digit nel primo campo
		if (rawVal.length > 1) {
			const digits = rawVal.slice(0, 6).split('');
			this.otpDigits.update(() => {
				const newDigits = ['', '', '', '', '', ''];
				for (let i = 0; i < digits.length; i++) {
					newDigits[i] = digits[i];
				}
				return newDigits;
			});
			// aggiorna tutti gli input DOM
			setTimeout(() => {
				for (let i = 0; i < 6; i++) {
					const el = document.getElementById(`otp-modal-${i}`) as HTMLInputElement;
					if (el) el.value = this.otpDigits()[i];
				}
				const nextIndex = Math.min(digits.length, 5);
				const next = document.getElementById(`otp-modal-${nextIndex}`) as HTMLInputElement;
				next?.focus();
			}, 0);
			return;
		}

		const digit = rawVal.slice(-1);

		this.otpDigits.update((digits) => {
			const newDigits = [...digits];
			newDigits[index] = digit;
			return newDigits;
		});

		input.value = digit;

		if (digit && index < 5) {
			const next = document.getElementById(`otp-modal-${index + 1}`) as HTMLInputElement;
			next?.focus();
		}
	}

	onOtpKeydown(index: number, event: KeyboardEvent) {
		if (event.key === 'Backspace' && !this.otpDigits()[index] && index > 0) {
			const prev = document.getElementById(`otp-modal-${index - 1}`) as HTMLInputElement;
			prev?.focus();
		}
	}

	onOtpPaste(event: ClipboardEvent) {
		const pastedText = event.clipboardData?.getData('text');
		if (!pastedText) return;

		const digits = pastedText.replace(/\D/g, '').slice(0, 6).split('');
		if (digits.length === 0) return;

		event.preventDefault();

		this.otpDigits.update(() => {
			const newDigits = ['', '', '', '', '', ''];
			for (let i = 0; i < digits.length; i++) {
				newDigits[i] = digits[i];
			}
			return newDigits;
		});

		const nextIndex = Math.min(digits.length, 5);
		setTimeout(() => {
			const next = document.getElementById(`otp-modal-${nextIndex}`) as HTMLInputElement;
			next?.focus();
		}, 0);
	}

	conferma() {
		if (!this.isOtpComplete()) return;
		this.activeModal.close(this.otpCode());
	}

	dismiss() {
		this.activeModal.dismiss();
	}
}
